import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  ArrowLeft, Upload, Plus, FileText, Download, Trash2, Edit, 
  CheckCircle, X, Book, Loader2, AlertCircle, FileSpreadsheet,
  Eye, Save, HelpCircle, Copy, Shield, Users, Clock, XCircle,
  Search, Filter, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import EdpiesLogo from '@/components/EdpiesLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Import unified taxonomy configuration
import { 
  YEAR_STAGES, 
  SUBJECTS, 
  DIFFICULTIES, 
  DIFFICULTY_LEVELS,
  SUBJECT_DOMAINS,
  QUESTION_TYPES,
  COGNITIVE_LEVELS,
  EXAM_BOARDS,
  getSubjectLabel,
  getStageLabel,
  getYearLabel,
  getDomainsForStageSubject,
  getStageFromYear
} from '../config/taxonomy';

// Format for selects
const stages = YEAR_STAGES.map(y => ({ value: y.value, label: y.label }));
const subjects = SUBJECTS.map(s => ({ value: s.id, label: s.name }));
const difficulties = DIFFICULTIES;

const AdminQuestionBanks = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [questionBanks, setQuestionBanks] = useState([]);
  const [activeTab, setActiveTab] = useState('banks');
  const [uploading, setUploading] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [editingBank, setEditingBank] = useState(null);
  const [editedQuestions, setEditedQuestions] = useState([]);
  const [savingBulk, setSavingBulk] = useState(false);
  
  // Tutor management state
  const [tutors, setTutors] = useState([]);
  const [pendingQuestions, setPendingQuestions] = useState([]);
  const [pendingPapers, setPendingPapers] = useState([]);
  const [loadingTutors, setLoadingTutors] = useState(false);
  
  // Duplicate modal state
  const [duplicateModal, setDuplicateModal] = useState(false);
  const [duplicatingBank, setDuplicatingBank] = useState(null);
  const [duplicateForm, setDuplicateForm] = useState({
    newName: '',
    newStage: '',
    newSubject: ''
  });
  const [duplicating, setDuplicating] = useState(false);
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    name: '',
    stage: '',
    subject: '',
    description: '',
    file: null
  });
  
  // Manual question form state
  const [questionForm, setQuestionForm] = useState({
    bankName: '',
    stage: '',
    subject: '',
    description: '',
    questions: [{
      question: '',
      options: ['', '', '', ''],
      correct_answer: 0,
      explanation: '',
      topic: '',
      difficulty: 'medium'
    }]
  });

  useEffect(() => {
    loadQuestionBanks();
    loadTutors();
    loadPendingQuestions();
    loadPendingPapers();
  }, []);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadQuestionBanks = async () => {
    try {
      const response = await axios.get(`${API}/admin/question-banks`, { headers });
      setQuestionBanks(response.data.banks || []);
    } catch (error) {
      console.error('Error loading question banks:', error);
      setQuestionBanks([]);
    }
    setLoading(false);
  };

  const loadTutors = async () => {
    setLoadingTutors(true);
    try {
      const response = await axios.get(`${API}/admin/tutors`, { headers });
      setTutors(response.data.tutors || []);
    } catch (error) {
      console.error('Error loading tutors:', error);
      setTutors([]);
    }
    setLoadingTutors(false);
  };

  const loadPendingQuestions = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-questions`, { headers });
      setPendingQuestions(response.data.questions || []);
    } catch (error) {
      console.error('Error loading pending questions:', error);
      setPendingQuestions([]);
    }
  };

  const loadPendingPapers = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-papers`, { headers });
      setPendingPapers(response.data.papers || []);
    } catch (error) {
      console.error('Error loading pending papers:', error);
      setPendingPapers([]);
    }
  };

  const approvePaper = async (paperId) => {
    try {
      await axios.post(`${API}/admin/approve-paper/${paperId}`, {}, { headers });
      toast.success('Paper approved!');
      loadPendingPapers();
    } catch (error) {
      toast.error('Failed to approve paper');
    }
  };

  const rejectPaper = async (paperId) => {
    try {
      await axios.post(`${API}/admin/reject-paper/${paperId}`, {}, { headers });
      toast.success('Paper rejected');
      loadPendingPapers();
    } catch (error) {
      toast.error('Failed to reject paper');
    }
  };

  const verifyTutor = async (tutorId, verify = true) => {
    try {
      const endpoint = verify ? 'verify-tutor' : 'unverify-tutor';
      await axios.post(`${API}/admin/${endpoint}/${tutorId}`, {}, { headers });
      toast.success(verify ? 'Tutor verified! They can now create questions without approval.' : 'Tutor verification removed');
      loadTutors();
    } catch (error) {
      toast.error('Failed to update tutor status');
    }
  };

  const approveQuestion = async (questionId) => {
    try {
      await axios.post(`${API}/admin/approve-question/${questionId}`, {}, { headers });
      toast.success('Question approved!');
      loadPendingQuestions();
    } catch (error) {
      toast.error('Failed to approve question');
    }
  };

  const rejectQuestion = async (questionId) => {
    const reason = window.prompt('Reason for rejection (optional):');
    try {
      await axios.post(`${API}/admin/reject-question/${questionId}?reason=${encodeURIComponent(reason || '')}`, {}, { headers });
      toast.success('Question rejected');
      loadPendingQuestions();
    } catch (error) {
      toast.error('Failed to reject question');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file || !uploadForm.name || !uploadForm.stage || !uploadForm.subject) {
      toast.error('Please fill in all required fields');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('name', uploadForm.name);
      formData.append('stage', uploadForm.stage);
      formData.append('subject', uploadForm.subject);
      formData.append('description', uploadForm.description);

      await axios.post(`${API}/admin/question-banks/upload`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Question bank uploaded successfully!');
      setUploadForm({ name: '', stage: '', subject: '', description: '', file: null });
      loadQuestionBanks();
      setActiveTab('banks');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload question bank');
    }
    setUploading(false);
  };

  const downloadTemplate = async (format) => {
    try {
      const response = await axios.get(`${API}/admin/question-banks/template?format=${format}`, {
        headers,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `question_template.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Downloaded ${format.toUpperCase()} template`);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const exportBank = async (bankId, format = 'csv') => {
    try {
      const response = await axios.get(`${API}/admin/question-banks/${bankId}/export?format=${format}`, {
        headers,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const bank = questionBanks.find(b => b.id === bankId);
      link.setAttribute('download', `${bank?.name || 'questions'}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Exported bank to ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export bank');
    }
  };

  const startBulkEdit = async (bankId) => {
    try {
      const response = await axios.get(`${API}/admin/question-banks/${bankId}`, { headers });
      setEditingBank(response.data);
      setEditedQuestions([...response.data.questions]);
      setActiveTab('bulk-edit');
    } catch (error) {
      toast.error('Failed to load bank for editing');
    }
  };

  const saveBulkEdits = async () => {
    setSavingBulk(true);
    try {
      await axios.put(`${API}/admin/question-banks/${editingBank.id}`, {
        questions: editedQuestions
      }, { headers });
      
      toast.success('Changes saved successfully!');
      setEditingBank(null);
      setEditedQuestions([]);
      loadQuestionBanks();
      setActiveTab('banks');
    } catch (error) {
      toast.error('Failed to save changes');
    }
    setSavingBulk(false);
  };

  const updateEditedQuestion = (index, field, value) => {
    const updated = [...editedQuestions];
    if (field.startsWith('options.')) {
      const optIndex = parseInt(field.split('.')[1]);
      updated[index].options[optIndex] = value;
    } else {
      updated[index][field] = value;
    }
    setEditedQuestions(updated);
  };

  const deleteBank = async (bankId) => {
    if (!window.confirm('Are you sure you want to delete this question bank?')) return;
    
    try {
      await axios.delete(`${API}/admin/question-banks/${bankId}`, { headers });
      toast.success('Question bank deleted');
      loadQuestionBanks();
    } catch (error) {
      toast.error('Failed to delete question bank');
    }
  };

  const toggleBankStatus = async (bankId, currentStatus) => {
    try {
      await axios.patch(`${API}/admin/question-banks/${bankId}/status`, {
        is_active: !currentStatus
      }, { headers });
      toast.success(`Bank ${!currentStatus ? 'activated' : 'deactivated'}`);
      loadQuestionBanks();
    } catch (error) {
      toast.error('Failed to update bank status');
    }
  };

  // Manual question creation
  const addQuestion = () => {
    setQuestionForm({
      ...questionForm,
      questions: [...questionForm.questions, {
        question: '',
        options: ['', '', '', ''],
        correct_answer: 0,
        explanation: '',
        topic: '',
        difficulty: 'medium'
      }]
    });
  };

  const removeQuestion = (index) => {
    if (questionForm.questions.length > 1) {
      setQuestionForm({
        ...questionForm,
        questions: questionForm.questions.filter((_, i) => i !== index)
      });
    }
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questionForm.questions];
    if (field.startsWith('options.')) {
      const optIndex = parseInt(field.split('.')[1]);
      updated[index].options[optIndex] = value;
    } else {
      updated[index][field] = value;
    }
    setQuestionForm({ ...questionForm, questions: updated });
  };

  const handleManualCreate = async (e) => {
    e.preventDefault();
    if (!questionForm.bankName || !questionForm.stage || !questionForm.subject) {
      toast.error('Please fill in all required fields');
      return;
    }

    setUploading(true);
    try {
      await axios.post(`${API}/admin/question-banks/manual`, {
        name: questionForm.bankName,
        stage: questionForm.stage,
        subject: questionForm.subject,
        description: questionForm.description,
        questions: questionForm.questions
      }, { headers });

      toast.success('Question bank created successfully!');
      setQuestionForm({
        bankName: '',
        stage: '',
        subject: '',
        description: '',
        questions: [{
          question: '',
          options: ['', '', '', ''],
          correct_answer: 0,
          explanation: '',
          topic: '',
          difficulty: 'medium'
        }]
      });
      loadQuestionBanks();
      setActiveTab('banks');
    } catch (error) {
      toast.error('Failed to create question bank');
    }
    setUploading(false);
  };

  // Duplicate bank
  const openDuplicateModal = (bank) => {
    setDuplicatingBank(bank);
    setDuplicateForm({
      newName: `${bank.name} (Copy)`,
      newStage: bank.stage,
      newSubject: bank.subject
    });
    setDuplicateModal(true);
  };

  const handleDuplicate = async () => {
    if (!duplicateForm.newName || !duplicateForm.newStage || !duplicateForm.newSubject) {
      toast.error('Please fill in all fields');
      return;
    }

    setDuplicating(true);
    try {
      await axios.post(`${API}/admin/question-banks/${duplicatingBank.id}/duplicate`, {
        name: duplicateForm.newName,
        stage: duplicateForm.newStage,
        subject: duplicateForm.newSubject
      }, { headers });

      toast.success('Question bank duplicated successfully!');
      setDuplicateModal(false);
      setDuplicatingBank(null);
      loadQuestionBanks();
    } catch (error) {
      toast.error('Failed to duplicate question bank');
    }
    setDuplicating(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Loader2 className="h-8 w-8 text-white animate-spin" />
      </div>
    );
  }

  const pendingCount = pendingQuestions.length + pendingPapers.length;
  const verifiedTutorsCount = tutors.filter(t => t.isVerifiedTutor).length;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      {/* Header - Mobile Responsive */}
      <header className="bg-white/10 backdrop-blur-sm shadow-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/tutors')} className="text-white hover:bg-white/20 px-2 sm:px-4">
              <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2" />
              <span className="hidden sm:inline">Admin</span>
            </Button>
            <div className="flex items-center gap-2">
              <Book className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              <h1 className="text-lg sm:text-2xl font-bold text-white">Question Bank</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {pendingCount > 0 && (
              <Badge className="bg-amber-500 text-xs sm:text-sm">
                {pendingCount} Pending
              </Badge>
            )}
            <EdpiesLogo size="sm" className="hidden sm:block" />
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-3 sm:p-4 md:p-8">
        {/* Stats Cards - Mobile Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
          <Card className="bg-white/90">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-purple-100 rounded-lg">
                  <Book className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold">{questionBanks.length}</p>
                  <p className="text-[10px] sm:text-xs text-gray-500">Banks</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/90">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-amber-100 rounded-lg">
                  <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold">{pendingCount}</p>
                  <p className="text-[10px] sm:text-xs text-gray-500">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/90">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-green-100 rounded-lg">
                  <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold">{verifiedTutorsCount}</p>
                  <p className="text-[10px] sm:text-xs text-gray-500">Verified</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white/90">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg">
                  <Users className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-lg sm:text-2xl font-bold">{tutors.length}</p>
                  <p className="text-[10px] sm:text-xs text-gray-500">Tutors</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Tabs - Mobile Scrollable */}
          <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0 mb-4 sm:mb-6">
            <TabsList className="inline-flex sm:grid sm:grid-cols-5 gap-1 bg-white/20 p-1 min-w-max sm:min-w-0 w-auto sm:w-full">
              <TabsTrigger value="banks" className="text-xs sm:text-sm whitespace-nowrap px-3 sm:px-4 data-[state=active]:bg-white">
                <FileText className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                Banks
              </TabsTrigger>
              <TabsTrigger value="upload" className="text-xs sm:text-sm whitespace-nowrap px-3 sm:px-4 data-[state=active]:bg-white">
                <Upload className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                Upload
              </TabsTrigger>
              <TabsTrigger value="manual" className="text-xs sm:text-sm whitespace-nowrap px-3 sm:px-4 data-[state=active]:bg-white">
                <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                Create
              </TabsTrigger>
              <TabsTrigger value="pending" className="text-xs sm:text-sm whitespace-nowrap px-3 sm:px-4 data-[state=active]:bg-white relative">
                <Clock className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                Pending
                {pendingCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                    {pendingCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="tutors" className="text-xs sm:text-sm whitespace-nowrap px-3 sm:px-4 data-[state=active]:bg-white">
                <Shield className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                Tutors
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Question Banks List */}
          <TabsContent value="banks">
            {questionBanks.length === 0 ? (
              <Card className="shadow-xl">
                <CardContent className="p-6 sm:p-12 text-center">
                  <FileText className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">No Question Banks Yet</h3>
                  <p className="text-gray-500 mb-6 text-sm sm:text-base">Upload a file or create questions manually to get started.</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button onClick={() => setActiveTab('upload')}>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload File
                    </Button>
                    <Button variant="outline" onClick={() => setActiveTab('manual')}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Manually
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3 sm:gap-4">
                {questionBanks.map((bank) => (
                  <Card key={bank.id} className="shadow-lg hover:shadow-xl transition-shadow bg-white">
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h3 className="text-base sm:text-xl font-bold text-gray-800">{bank.name}</h3>
                            <Badge className={bank.is_active ? 'bg-green-500' : 'bg-gray-400'}>
                              {bank.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                          <p className="text-gray-600 mb-3 text-sm">{bank.description || 'No description'}</p>
                          <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3">
                            <Badge variant="outline" className="text-xs">{stages.find(s => s.value === bank.stage)?.label || bank.stage}</Badge>
                            <Badge variant="outline" className="text-xs">{subjects.find(s => s.value === bank.subject)?.label || bank.subject}</Badge>
                            <Badge variant="secondary" className="text-xs">{bank.question_count} questions</Badge>
                          </div>
                          <p className="text-xs sm:text-sm text-gray-500">
                            Created: {new Date(bank.created_at).toLocaleDateString()}
                            {bank.source_file && ` • ${bank.source_file}`}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" variant="outline" className="text-blue-600 text-xs" onClick={() => startBulkEdit(bank.id)}>
                            <Edit className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
                            <span className="hidden sm:inline">Edit</span>
                          </Button>
                          <Button size="sm" variant="outline" className="text-orange-600 text-xs" onClick={() => openDuplicateModal(bank)}>
                            <Copy className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
                            <span className="hidden sm:inline">Copy</span>
                          </Button>
                          <Button size="sm" variant="outline" className="text-green-600 text-xs" onClick={() => exportBank(bank.id)}>
                            <Download className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
                            <span className="hidden sm:inline">Export</span>
                          </Button>
                          <Button size="sm" variant="outline" className="text-xs" onClick={() => toggleBankStatus(bank.id, bank.is_active)}>
                            {bank.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600 text-xs" onClick={() => deleteBank(bank.id)}>
                            <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Upload Tab */}
          <TabsContent value="upload">
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="text-lg sm:text-xl">Upload Question Bank</CardTitle>
                <CardDescription className="text-sm">Upload CSV, JSON, or PDF files with questions</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpload} className="space-y-4 sm:space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Bank Name *</Label>
                      <Input
                        value={uploadForm.name}
                        onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                        placeholder="e.g., GCSE Maths Practice Set 1"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Stage / Year *</Label>
                      <Select value={uploadForm.stage} onValueChange={(v) => setUploadForm({ ...uploadForm, stage: v })}>
                        <SelectTrigger><SelectValue placeholder="Select stage" /></SelectTrigger>
                        <SelectContent>
                          {stages.map((s) => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Subject *</Label>
                      <Select value={uploadForm.subject} onValueChange={(v) => setUploadForm({ ...uploadForm, subject: v })}>
                        <SelectTrigger><SelectValue placeholder="Select subject" /></SelectTrigger>
                        <SelectContent>
                          {subjects.map((s) => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input
                        value={uploadForm.description}
                        onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                        placeholder="Optional description"
                      />
                    </div>
                  </div>

                  {/* File Upload Area */}
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 sm:p-8 text-center hover:border-purple-500 transition-colors">
                    <input
                      type="file"
                      id="file-upload"
                      className="hidden"
                      accept=".csv,.json,.pdf,.xlsx,.xls"
                      onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      {uploadForm.file ? (
                        <div className="space-y-2">
                          <FileText className="h-10 w-10 sm:h-12 sm:w-12 mx-auto text-purple-500" />
                          <p className="font-medium text-sm sm:text-base">{uploadForm.file.name}</p>
                          <p className="text-gray-500 text-xs sm:text-sm">{(uploadForm.file.size / 1024).toFixed(2)} KB</p>
                          <Button type="button" variant="ghost" size="sm" onClick={(e) => { e.preventDefault(); setUploadForm({ ...uploadForm, file: null }); }}>
                            <X className="h-4 w-4 mr-1" /> Remove
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Upload className="h-10 w-10 sm:h-12 sm:w-12 mx-auto text-gray-400" />
                          <p className="font-medium text-sm sm:text-base">Click to upload or drag and drop</p>
                          <p className="text-gray-500 text-xs sm:text-sm">CSV, JSON, PDF, Excel (max 10MB)</p>
                        </div>
                      )}
                    </label>
                  </div>

                  {/* Template Downloads */}
                  <div className="flex flex-wrap gap-2">
                    <span className="text-sm text-gray-500">Download templates:</span>
                    <Button type="button" variant="link" size="sm" onClick={() => downloadTemplate('csv')} className="text-xs">CSV</Button>
                    <Button type="button" variant="link" size="sm" onClick={() => downloadTemplate('json')} className="text-xs">JSON</Button>
                    <Button type="button" variant="link" size="sm" onClick={() => downloadTemplate('xlsx')} className="text-xs">Excel</Button>
                  </div>

                  <Button type="submit" disabled={uploading} className="w-full">
                    {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                    Upload Question Bank
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Manual Create Tab */}
          <TabsContent value="manual">
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="text-lg sm:text-xl">Create Questions Manually</CardTitle>
                <CardDescription className="text-sm">Add questions one by one</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleManualCreate} className="space-y-4 sm:space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Bank Name *</Label>
                      <Input
                        value={questionForm.bankName}
                        onChange={(e) => setQuestionForm({ ...questionForm, bankName: e.target.value })}
                        placeholder="e.g., Year 6 Maths Practice"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Stage / Year *</Label>
                      <Select value={questionForm.stage} onValueChange={(v) => setQuestionForm({ ...questionForm, stage: v })}>
                        <SelectTrigger><SelectValue placeholder="Select stage" /></SelectTrigger>
                        <SelectContent>
                          {stages.map((s) => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Subject *</Label>
                      <Select value={questionForm.subject} onValueChange={(v) => setQuestionForm({ ...questionForm, subject: v })}>
                        <SelectTrigger><SelectValue placeholder="Select subject" /></SelectTrigger>
                        <SelectContent>
                          {subjects.map((s) => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input
                        value={questionForm.description}
                        onChange={(e) => setQuestionForm({ ...questionForm, description: e.target.value })}
                        placeholder="Optional description"
                      />
                    </div>
                  </div>

                  {/* Questions */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-semibold">Questions ({questionForm.questions.length})</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addQuestion}>
                        <Plus className="h-4 w-4 mr-1" /> Add Question
                      </Button>
                    </div>

                    {questionForm.questions.map((q, qIdx) => (
                      <Card key={qIdx} className="border-2">
                        <CardHeader className="pb-2 flex flex-row items-center justify-between">
                          <span className="font-semibold text-sm">Question {qIdx + 1}</span>
                          {questionForm.questions.length > 1 && (
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeQuestion(qIdx)} className="text-red-500">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <Textarea
                            value={q.question}
                            onChange={(e) => updateQuestion(qIdx, 'question', e.target.value)}
                            placeholder="Enter question text..."
                            rows={2}
                          />
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {q.options.map((opt, optIdx) => (
                              <div key={optIdx} className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => updateQuestion(qIdx, 'correct_answer', optIdx)}
                                  className={`w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs ${
                                    q.correct_answer === optIdx ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'
                                  }`}
                                >
                                  {String.fromCharCode(65 + optIdx)}
                                </button>
                                <Input
                                  value={opt}
                                  onChange={(e) => updateQuestion(qIdx, `options.${optIdx}`, e.target.value)}
                                  placeholder={`Option ${String.fromCharCode(65 + optIdx)}`}
                                  className="flex-1 text-sm"
                                />
                              </div>
                            ))}
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <Input
                              value={q.topic}
                              onChange={(e) => updateQuestion(qIdx, 'topic', e.target.value)}
                              placeholder="Topic (optional)"
                              className="text-sm"
                            />
                            <Select value={q.difficulty} onValueChange={(v) => updateQuestion(qIdx, 'difficulty', v)}>
                              <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {difficulties.map((d) => (
                                  <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <Textarea
                            value={q.explanation}
                            onChange={(e) => updateQuestion(qIdx, 'explanation', e.target.value)}
                            placeholder="Explanation (optional)"
                            rows={2}
                            className="text-sm"
                          />
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  <Button type="submit" disabled={uploading} className="w-full">
                    {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Create Question Bank
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pending Approval Tab */}
          <TabsContent value="pending">
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                  Pending Approval ({pendingCount})
                </CardTitle>
                <CardDescription className="text-sm">Questions and papers submitted by tutors awaiting your review</CardDescription>
              </CardHeader>
              <CardContent>
                {pendingCount === 0 ? (
                  <div className="text-center py-8 sm:py-12">
                    <CheckCircle className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-4 text-green-300" />
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">All Caught Up!</h3>
                    <p className="text-gray-500 text-sm">No pending items to review</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Pending Papers Section */}
                    {pendingPapers.length > 0 && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                            <Upload className="h-4 w-4" />
                            Uploaded Papers ({pendingPapers.length})
                          </h3>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => navigate('/admin/papers')}
                            className="text-xs"
                          >
                            View All Papers
                          </Button>
                        </div>
                        {pendingPapers.map((paper) => (
                          <Card key={paper.id} className="border-l-4 border-l-blue-500">
                            <CardContent className="p-4">
                              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <FileText className="h-5 w-5 text-blue-500" />
                                    <p className="font-medium text-gray-800">{paper.name}</p>
                                  </div>
                                  <p className="text-sm text-gray-600 mb-2">{paper.description || 'No description'}</p>
                                  <div className="flex flex-wrap gap-1.5">
                                    <Badge variant="outline" className="text-xs">{paper.stage}</Badge>
                                    <Badge variant="outline" className="text-xs">{paper.subject}</Badge>
                                    <Badge variant="secondary" className="text-xs">By: {paper.uploadedByName || 'Tutor'}</Badge>
                                    <Badge variant="secondary" className="text-xs">{paper.fileName}</Badge>
                                  </div>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2 mt-3">
                                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-xs" onClick={() => approvePaper(paper.id)}>
                                  <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1" /> Approve
                                </Button>
                                <Button size="sm" variant="destructive" className="text-xs" onClick={() => rejectPaper(paper.id)}>
                                  <XCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1" /> Reject
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}

                    {/* Pending Questions Section */}
                    {pendingQuestions.length > 0 && (
                      <div className="space-y-3">
                        <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                          <HelpCircle className="h-4 w-4" />
                          Individual Questions ({pendingQuestions.length})
                        </h3>
                        {pendingQuestions.map((q) => (
                          <Card key={q.id} className="border-l-4 border-l-amber-500">
                            <CardContent className="p-4">
                              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                <div className="flex-1">
                                  <p className="font-medium text-gray-800 text-sm sm:text-base">{q.question}</p>
                                  <div className="flex flex-wrap gap-1.5 mt-2">
                                    <Badge variant="outline" className="text-xs">{q.stage}</Badge>
                                    <Badge variant="outline" className="text-xs">{subjects.find(s => s.value === q.subject)?.label || q.subject}</Badge>
                                    <Badge variant="secondary" className="text-xs">By: {q.creatorName || 'Tutor'}</Badge>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Options */}
                              <div className="grid grid-cols-2 gap-2 my-3">
                                {q.options?.map((opt, i) => (
                                  <div key={i} className={`p-2 rounded text-xs sm:text-sm ${i === q.correctAnswer ? 'bg-green-100 border border-green-300' : 'bg-gray-50'}`}>
                                    <span className="font-medium mr-1">{String.fromCharCode(65 + i)}.</span>
                                    {opt}
                                  </div>
                                ))}
                              </div>
                              
                              {q.explanation && (
                                <p className="text-xs sm:text-sm text-gray-600 mb-3">
                                  <strong>Explanation:</strong> {q.explanation}
                                </p>
                              )}
                              
                              <div className="flex flex-wrap gap-2">
                                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-xs" onClick={() => approveQuestion(q.id)}>
                                  <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1" /> Approve
                                </Button>
                                <Button size="sm" variant="destructive" className="text-xs" onClick={() => rejectQuestion(q.id)}>
                                  <XCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1" /> Reject
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tutor Verification Tab */}
          <TabsContent value="tutors">
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  <Shield className="h-5 w-5 text-indigo-500" />
                  Tutor Verification
                </CardTitle>
                <CardDescription className="text-sm">
                  Verified tutors can create questions without admin approval
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingTutors ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                  </div>
                ) : tutors.length === 0 ? (
                  <div className="text-center py-8 sm:py-12">
                    <Users className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">No Tutors Yet</h3>
                    <p className="text-gray-500 text-sm">Tutors will appear here once registered</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {tutors.map((tutor) => (
                      <Card key={tutor.id} className={tutor.isVerifiedTutor ? 'border-l-4 border-l-green-500' : ''}>
                        <CardContent className="p-4">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <Users className="h-5 w-5 text-indigo-600" />
                              </div>
                              <div>
                                <p className="font-medium text-sm sm:text-base">{tutor.name}</p>
                                <p className="text-xs sm:text-sm text-gray-500">{tutor.email}</p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {tutor.subjects?.slice(0, 3).map(s => (
                                    <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>
                                  ))}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 sm:gap-3">
                              {tutor.isVerifiedTutor ? (
                                <>
                                  <Badge className="bg-green-100 text-green-700 text-xs">
                                    <Shield className="h-3 w-3 mr-1" /> Verified
                                  </Badge>
                                  <Button size="sm" variant="outline" className="text-xs" onClick={() => verifyTutor(tutor.id, false)}>
                                    Remove
                                  </Button>
                                </>
                              ) : (
                                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-xs" onClick={() => verifyTutor(tutor.id, true)}>
                                  <Shield className="h-3 w-3 sm:h-4 sm:w-4 mr-1" /> Verify
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bulk Edit Tab */}
          <TabsContent value="bulk-edit">
            {editingBank && (
              <Card className="shadow-xl">
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <CardTitle className="text-lg sm:text-xl">Editing: {editingBank.name}</CardTitle>
                      <CardDescription className="text-sm">{editedQuestions.length} questions</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => { setEditingBank(null); setActiveTab('banks'); }}>
                        Cancel
                      </Button>
                      <Button onClick={saveBulkEdits} disabled={savingBulk}>
                        {savingBulk ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                        Save Changes
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                    {editedQuestions.map((q, idx) => (
                      <Card key={idx} className="border">
                        <CardContent className="p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-sm">Q{idx + 1}</span>
                            <Badge variant="outline" className="text-xs">{q.difficulty}</Badge>
                          </div>
                          <Textarea
                            value={q.question}
                            onChange={(e) => updateEditedQuestion(idx, 'question', e.target.value)}
                            rows={2}
                            className="text-sm"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            {q.options.map((opt, optIdx) => (
                              <div key={optIdx} className="flex items-center gap-1">
                                <button
                                  onClick={() => updateEditedQuestion(idx, 'correct_answer', optIdx)}
                                  className={`w-6 h-6 rounded text-xs font-bold ${q.correct_answer === optIdx ? 'bg-green-500 text-white' : 'bg-gray-200'}`}
                                >
                                  {String.fromCharCode(65 + optIdx)}
                                </button>
                                <Input
                                  value={opt}
                                  onChange={(e) => updateEditedQuestion(idx, `options.${optIdx}`, e.target.value)}
                                  className="flex-1 text-xs"
                                />
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Duplicate Modal */}
      <Dialog open={duplicateModal} onOpenChange={setDuplicateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Duplicate Question Bank</DialogTitle>
            <DialogDescription>Create a copy with different settings</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Name</Label>
              <Input
                value={duplicateForm.newName}
                onChange={(e) => setDuplicateForm({ ...duplicateForm, newName: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Stage</Label>
              <Select value={duplicateForm.newStage} onValueChange={(v) => setDuplicateForm({ ...duplicateForm, newStage: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {stages.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Subject</Label>
              <Select value={duplicateForm.newSubject} onValueChange={(v) => setDuplicateForm({ ...duplicateForm, newSubject: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {subjects.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateModal(false)}>Cancel</Button>
            <Button onClick={handleDuplicate} disabled={duplicating}>
              {duplicating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Copy className="h-4 w-4 mr-2" />}
              Duplicate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminQuestionBanks;
